"use client";

import React, { useState } from "react";
import { Button } from "./button.js";
import { Badge } from "./badge.js";
import { Bug, ChevronDown, ChevronUp, Send } from "lucide-react";
import { CalendarEvent } from "../types/calendar.js";
import { ResponsiveModal } from "./responsive-modal.js";
import { Accordion, AccordionContent, AccordionItemWithContext, AccordionTrigger } from "./accordion.js";
import { Input } from "./input.js";
import { Textarea } from "./textarea.js";
import { Label } from "./label.js";
import { toast } from "sonner";

interface DiagnosticButtonProps {
  // Support both interfaces for backward compatibility
  event?: CalendarEvent;
  extractComponentProps?: string;
  maxDepth?: number;
  additionalMetadata?: Record<string, any>;
  // Task creation function - should be provided by the parent component
  onCreateTask?: (taskData: any) => Promise<any>;
}

// Task creation interface
interface TaskCreationData {
  title: string;
  description: string;
  contact_id?: string;
  status: string;
  priority: string;
  assigned_to: string;
  assigned_users: string[];
  due_date?: string;
  tags?: string[];
}

export function DiagnosticButton({ 
  event,
  extractComponentProps: extractProps, 
  maxDepth = 3, 
  additionalMetadata,
  onCreateTask
}: DiagnosticButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issueDescription, setIssueDescription] = useState("");

  const extractComponentProps = (obj: any, maxDepth = 3, currentDepth = 0): any => {
    if (currentDepth >= maxDepth || obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.slice(0, 5).map(item => extractComponentProps(item, maxDepth, currentDepth + 1));
    }

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith('_') || key === 'password' || key === 'token') {
        result[key] = '[HIDDEN]';
      } else {
        result[key] = extractComponentProps(value, maxDepth, currentDepth + 1);
      }
    }
    
    return result;
  };

  // Build diagnostic data based on available props
  const diagnosticData: Record<string, any> = {
    timestamp: new Date().toISOString(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Server',
    url: typeof window !== 'undefined' ? window.location.href : 'Server',
    ...additionalMetadata
  };

  // Add event data if available
  if (event) {
    diagnosticData.event = extractComponentProps(event, 5);
  }

  // Add extractComponentProps and maxDepth if provided
  if (extractProps) {
    diagnosticData.extractComponentProps = extractProps;
    diagnosticData.maxDepth = maxDepth;
  }

  const handleCreateTask = async () => {
    if (!issueDescription.trim()) {
      toast.error("Please describe the issue before submitting.");
      return;
    }

    if (!onCreateTask) {
      toast.error("Task creation function not available. Please contact support.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create task data
      const taskData: TaskCreationData = {
        title: `Diagnostic Issue Report - ${new Date().toLocaleDateString()}`,
        description: `**Issue Description:**\n${issueDescription}\n\n**Diagnostic Data:**\n\`\`\`json\n${JSON.stringify(diagnosticData, null, 2)}\n\`\`\``,
        contact_id: additionalMetadata?.contactId || undefined,
        status: "pending",
        priority: "high",
        assigned_to: "01d247c0-8293-44bc-9961-06a4cc043dd3", // miclainkeffeler@westtn.consulting
        assigned_users: ["01d247c0-8293-44bc-9961-06a4cc043dd3"],
        tags: ["diagnostic", "bug-report", "auto-generated"]
      };

      // Use the provided task creation function
      await onCreateTask(taskData);

      // Success feedback
      toast.success("Diagnostic report submitted successfully! A task has been created for the development team.");
      
      // Close modal and reset form
      setIsModalOpen(false);
      setIssueDescription("");
      
    } catch (error) {
      console.error("Error creating diagnostic task:", error);
      toast.error("Failed to submit diagnostic report. Please try again or contact support.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (process.env.NODE_ENV === 'production') {
    return null; // Don't show in production
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsModalOpen(true)}
        className="flex items-center space-x-2"
      >
        <Bug className="h-4 w-4" />
        <span>Report Issue</span>
      </Button>

      <ResponsiveModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Diagnostic Issue Report"
        description="Report an issue with diagnostic information"
        maxWidth="4xl"
        className="max-h-[90vh]"
      >
        <div className="flex flex-col h-full">
          <div className="space-y-6 max-h-[60vh] overflow-y-auto scroll-smooth pb-4 flex-1">
          {/* Issue Description */}
          <div className="space-y-2">
            <Label htmlFor="issue-description">Describe the issue *</Label>
            <Textarea
              id="issue-description"
              placeholder="Please describe what issue you encountered, what you were trying to do, and any relevant details..."
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              className="min-h-[100px]"
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              This description will be included in the task created for the development team.
            </p>
          </div>

          {/* Diagnostic Data Accordion */}
          <div className="space-y-2">
            <Label>Diagnostic Information</Label>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItemWithContext value="system-info">
                <AccordionTrigger className="text-sm">
                  System Information
                </AccordionTrigger>
                <AccordionContent>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60 scroll-smooth">
                    {JSON.stringify({
                      timestamp: diagnosticData.timestamp,
                      userAgent: diagnosticData.userAgent,
                      url: diagnosticData.url
                    }, null, 2)}
                  </pre>
                </AccordionContent>
              </AccordionItemWithContext>

              {event && (
                <AccordionItemWithContext value="event-data">
                  <AccordionTrigger className="text-sm">
                    Event Data
                  </AccordionTrigger>
                  <AccordionContent>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60 scroll-smooth">
                      {JSON.stringify(diagnosticData.event, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItemWithContext>
              )}

              {extractProps && (
                <AccordionItemWithContext value="component-props">
                  <AccordionTrigger className="text-sm">
                    Component Props
                  </AccordionTrigger>
                  <AccordionContent>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60 scroll-smooth">
                      {JSON.stringify({
                        extractComponentProps: diagnosticData.extractComponentProps,
                        maxDepth: diagnosticData.maxDepth
                      }, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItemWithContext>
              )}

              {additionalMetadata && Object.keys(additionalMetadata).length > 0 && (
                <AccordionItemWithContext value="additional-metadata">
                  <AccordionTrigger className="text-sm">
                    Additional Metadata
                  </AccordionTrigger>
                  <AccordionContent>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60 scroll-smooth">
                      {JSON.stringify(additionalMetadata, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItemWithContext>
              )}

              <AccordionItemWithContext value="full-diagnostic">
                <AccordionTrigger className="text-sm">
                  Full Diagnostic Data
                </AccordionTrigger>
                <AccordionContent>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60 scroll-smooth">
                    {JSON.stringify(diagnosticData, null, 2)}
                  </pre>
                </AccordionContent>
              </AccordionItemWithContext>
            </Accordion>
          </div>

          {/* Information about task creation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>What happens when you submit:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-1 space-y-1">
              <li>• A high-priority task will be created in the system</li>
              <li>• The task will be assigned to the development team</li>
              <li>• All diagnostic information will be included in the task description</li>
              <li>• You'll receive confirmation when the task is created</li>
            </ul>
          </div>
          </div>
          
          {/* Submit Buttons - Always Visible */}
          <div className="flex gap-2 pt-4 border-t bg-background sticky bottom-0">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={isSubmitting || !issueDescription.trim()}
              className="flex-1 flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}

export default DiagnosticButton;